import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import maplibregl, {
  AttributionControl,
  LngLatBoundsLike,
  Map as MapLibreMap,
  NavigationControl,
  ScaleControl,
} from "maplibre-gl"
import "maplibre-theme/icons.default.css"
import "maplibre-theme/classic.css"
import { MapContext } from "@/components/maps/mapLibre/MapContext"
import type MapManager from "@/components/maps/MapManager"
import {
  MAP_STYLES,
  MAPTILER_KEY,
  MapStyle,
} from "@/components/maps/MapManager"
import { useDarkMode } from "@/contexts/DarkModeContext"
import { attachRegionBorders } from "@/components/maps/regionBorders"

export type LightMapViewHandle = {
  fitBounds: (bounds: LngLatBoundsLike, maxZoom?: number) => void
  getMap: () => MapLibreMap | null
}

type LightMapViewProps = {
  children?: React.ReactNode
  fitBoundsTo?: LngLatBoundsLike | null
  initialCenter?: [number, number]
  initialZoom?: number
  mapStyle?: MapStyle
  className?: string
}

const LightMapView = forwardRef<LightMapViewHandle, LightMapViewProps>(
  (
    {
      children,
      fitBoundsTo,
      initialCenter = [20.0, 50.0],
      initialZoom = 5,
      mapStyle = "default",
      className,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const appliedStyleUrlRef = useRef<string>("")
    const [map, setMap] = useState<MapLibreMap | null>(null)
    const { isDarkMode } = useDarkMode()

    const styleUrl = useMemo(() => {
      const entry = MAP_STYLES[mapStyle]
      const base = isDarkMode ? entry.darkUrl : entry.url
      return entry.tilesProvider === "maptiler"
        ? `${base}?key=${MAPTILER_KEY}`
        : base
    }, [mapStyle, isDarkMode])

    // Stable shim that satisfies <Source>/<Layer>/<Marker> via MapContext.
    const contextValue = useMemo(
      () => ({
        mapManager: map
          ? ({ getMapInstance: () => map } as unknown as MapManager)
          : null,
      }),
      [map],
    )

    // Imperative handle for consumers (e.g. fit-all button).
    useImperativeHandle(
      ref,
      () => ({
        fitBounds: (bounds, maxZoom = 14) => {
          if (!map) return
          map.fitBounds(bounds, { padding: 40, duration: 600, maxZoom })
        },
        getMap: () => map,
      }),
      [map],
    )

    // Create the maplibre instance once. Style swaps go through
    // map.setStyle() below so pan/zoom/bearing/pitch are preserved.
    useEffect(() => {
      if (!containerRef.current) return
      const instance = new maplibregl.Map({
        container: containerRef.current,
        style: styleUrl,
        center: initialCenter,
        zoom: initialZoom,
        attributionControl: false,
      })
      instance.addControl(
        new NavigationControl({
          showZoom: true,
          showCompass: true,
          visualizePitch: true,
          visualizeRoll: true,
        }),
        "top-right",
      )
      instance.addControl(
        new AttributionControl({ compact: true }),
        "bottom-right",
      )
      instance.addControl(new ScaleControl({ unit: "metric" }), "bottom-right")
      appliedStyleUrlRef.current = styleUrl
      setMap(instance)
      return () => {
        instance.remove()
        setMap(null)
      }
      // Map is created once. styleUrl, initialCenter, and initialZoom only
      // seed the first render — later changes are handled by the setStyle
      // effect below (style) or ignored (center/zoom) so the user keeps
      // their viewport.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Swap style in place when mapStyle or dark mode changes. The Source
    // and Layer wrappers re-register themselves on the styledata event
    // emitted by setStyle, so user-added geojson/lines reappear. Markers
    // are DOM-attached and persist unchanged.
    useEffect(() => {
      if (!map) return
      if (appliedStyleUrlRef.current === styleUrl) return
      appliedStyleUrlRef.current = styleUrl
      map.setStyle(styleUrl)
    }, [map, styleUrl])

    useEffect(() => {
      if (!map) return
      return attachRegionBorders(map)
    }, [map])

    // ResizeObserver — keep canvas in sync with container size.
    useEffect(() => {
      if (!map || !containerRef.current) return
      const ro = new ResizeObserver(() => map.resize())
      ro.observe(containerRef.current)
      return () => ro.disconnect()
    }, [map])

    // Fit bounds when they change.
    useEffect(() => {
      if (!map || !fitBoundsTo) return
      const apply = () => {
        map.fitBounds(fitBoundsTo, { padding: 40, duration: 600, maxZoom: 14 })
      }
      if (map.isStyleLoaded()) {
        apply()
      } else {
        map.once("idle", apply)
        return () => {
          map.off("idle", apply)
        }
      }
      return undefined
    }, [map, fitBoundsTo])

    return (
      <MapContext.Provider value={contextValue}>
        <div
          ref={containerRef}
          className={className}
          style={{ width: "100%", height: "100%" }}
        />
        {map && children}
      </MapContext.Provider>
    )
  },
)

LightMapView.displayName = "LightMapView"

export default LightMapView
